import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from './axiosConfig';
import errorHandler from './utils/errorHandler';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import salonLogo from './Salon Logic S.jpg';

const PasswordRecovery = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');
    
    try {
      errorHandler.logDebug('PasswordRecovery', 'Sending password reset request');
      await api.post('/auth/request_password_reset', { email: email.trim().toLowerCase() });
      
      setMessage('If an account exists with this email, you will receive a password reset link shortly. Please check your inbox and spam folder.');
      errorHandler.logDebug('PasswordRecovery', 'Password reset request sent successfully');
    } catch (error) {
      errorHandler.logError('PasswordRecovery', 'Password reset request failed', error);
      setError(error.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        {/* Logo and Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src={salonLogo}
            alt="Salon Logic Pro Logo"
            sx={{ 
              width: 80, 
              height: 80, 
              mb: 2,
              objectFit: 'contain'
            }}
          />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Password Recovery
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Enter your email address and we'll send you a link to reset your password
          </Typography>
        </Box>

        {/* Main Card */}
        <Card elevation={4}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LockResetIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Reset Your Password
              </Typography>
            </Box>

            {message && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading || !!message}
                autoComplete="email"
                autoFocus
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isLoading || !!message}
                sx={{ py: 1.5, mb: 2 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Button 
                  startIcon={<ArrowBackIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Back to Login
                </Button>
              </Link>
            </Box>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Paper elevation={1} sx={{ mt: 3, p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Need help?</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            If you're having trouble resetting your password, please contact our support team at{' '}
            <a href="mailto:support@salonlogicpro.co.uk" style={{ color: 'inherit', fontWeight: 600 }}>
              support@salonlogicpro.co.uk
            </a>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default PasswordRecovery;